import {ChangeEvent, memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody, Checkbox} from "@material-tailwind/react";
import Card from "../../../components/Card";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {
    PaymentMethod,
    PaymentMethodsResponse,
    PaymentMethodTypesResponse
} from "../../../types/Responses/PaymentMethodsResponse";
import ProviderAvatar from "../../../components/ProviderAvatar";
import StatusLabel from "../../../components/StatusLabel";
import FilterDropdown from "../../../components/FilterDropdown";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import useComponentVisible from "../../../hooks/useComponentVisible";
import Modal from "../../../components/Modal/Modal";
import Loader from "../../../assets/icons/Loader";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import {AllRegionsResponse} from "../../../types/Responses/RegionsResponse";
import DropdownInput from "../../../components/DropdownInput";
import CountryAvatar from "../../../components/CountryAvatar";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {setSidebarTitle} from "../../../reducers/sidebar";
import {useDispatch} from "react-redux";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";

type PaymentMethodsDataTableRequest = DataTableRequest & { status?: number, provider?: number, type?: number };

const PaymentMethods = () => {
    const {t} = useTranslation();
    const user = useUserContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const system = useSystemContext();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsResponse>();
    const [types, setTypes] = useState<PaymentMethodTypesResponse>();
    const [listCountries, setListCountries] = useState<CountriesShortResponse>();
    const [allRegions, setAllRegions] = useState<AllRegionsResponse>();
    const [regions, setRegions] = useState<number[]>([]);

    const [bulkSelected, setBulkSelected] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [bulkStatus, setBulkStatus] = useState<number>();
    const [bulkType, setBulkType] = useState<number>();
    const [bulkProvider, setBulkProvider] = useState<number>();
    const [bulkCountries, setBulkCountries] = useState<number[]>([]);
    const [bulkCurrencies, setBulkCurrencies] = useState<number[]>([]);
    const [bulkNewTab, setBulkNewTab] = useState<boolean>(false);
    const [bulkInProgress, setBulkInProgress] = useState<boolean>(false);


    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const defaultRequest = useMemo<PaymentMethodsDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<PaymentMethodsDataTableRequest>(defaultRequest);
    const filterStatus = dataTableRequest.status ?? 0;
    const filterProvider = dataTableRequest.provider ?? 0;
    const filterType = dataTableRequest.type ?? 0;

    const modals = {
        status: useComponentVisible(false),
        type: useComponentVisible(false),
        provider: useComponentVisible(false),
        open_in_new_tab: useComponentVisible(false),
        countries: useComponentVisible(false),
        currencies: useComponentVisible(false)
    }

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        if (keys.includes('provider') && typeof request['provider'] !== "undefined" && parseInt(request['provider']) === 0)
            delete request['provider'];
        if (keys.includes('type') && typeof request['type'] !== "undefined" && parseInt(request['type']) === 0)
            delete request['type'];
        if (keys.includes('status') && typeof request['status'] !== "undefined" && parseInt(request['status']) === 0)
            delete request['status'];
        return request;
    }

    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true);
        API.getPaymentMethods(clearRequest(request)).then((response: AxiosResponse) => {
            setPaymentMethods(response.data as PaymentMethodsResponse);
            setIsDataTableLoading(false)
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYMENT_METHODS}`, toast, handleError, setIsForbidden));
    }, [toast, handleError]);

    const sendTypesRequest = useCallback(() => {
        API.getPaymentMethodTypes().then((response: AxiosResponse) => {
            setTypes(response.data as PaymentMethodTypesResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYMENT_METHOD_TYPES}`, toast, handleError));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: PaymentMethodsDataTableRequest) => {
        const updatedRequest = {
            ...request,
            status: filterStatus,
            provider: filterProvider,
            type: filterType
        };
        persistDataTableRequest(updatedRequest);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...updatedRequest, search: ''} : updatedRequest);
        }
    }, [persistDataTableRequest, filterProvider, filterStatus, filterType, sendRequest]);

    const onStatusChanged = useCallback((s: string | number) => {
        const st = typeof s === 'string' ? parseInt(s) : s;
        const request = {
            ...dataTableRequest,
            page: 1,
            status: st,
            provider: filterProvider,
            type: filterType,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterProvider, filterType, dataTableRequest, sendRequest, persistDataTableRequest]);

    const onProviderChanged = useCallback((s: string | number) => {
        const st = typeof s === 'string' ? parseInt(s) : s;
        const request = {
            ...dataTableRequest,
            page: 1,
            status: filterStatus,
            provider: st,
            type: filterType,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterStatus, filterType, dataTableRequest, sendRequest, persistDataTableRequest]);

    const onTypeChanged = useCallback((s: string | number) => {
        const st = typeof s === 'string' ? parseInt(s) : s;
        const request = {
            ...dataTableRequest,
            page: 1,
            status: filterStatus,
            provider: filterProvider,
            type: st,
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [filterStatus, filterProvider, dataTableRequest, sendRequest, persistDataTableRequest]);

    const sendCountriesRequest = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            setListCountries(response.data as CountriesShortResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const sendRegionsRequest = useCallback(() => {
        API.getRegionsShortList().then((response: AxiosResponse) => {
            setAllRegions(response.data as AllRegionsResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_REGIONS_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const deletePaymentMethod = useCallback((paymentMethod: PaymentMethod) => {
        if (paymentMethods) {
            const filteredArray = {
                ...paymentMethods,
                data: paymentMethods.data.filter(r => r.id !== paymentMethod.id),
                count: paymentMethods.count - 1
            };
            API.deletePaymentMethod(paymentMethod.id).then(() => {
                if (paymentMethod) {
                    setPaymentMethods(filteredArray)
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.UPDATE_PAYMENT_METHOD.replace('$paymentMethod', paymentMethod.id.toString())}`, toast, handleError));
        }
    }, [paymentMethods, setPaymentMethods, toast, handleError]);

    const onClickSelectAll = useCallback(() => {
        if (!paymentMethods)
            return;
        if (selectAll) {
            setSelectAll(false);
            setBulkSelected([]);
        } else {
            setSelectAll(true);
            setBulkSelected(paymentMethods.data.map(d => d.id));
        }
    }, [selectAll, bulkSelected, paymentMethods]);

    const onCheck = useCallback((id: number) => {
        if (bulkSelected.includes(id)) {
            setSelectAll(false);
            setBulkSelected(bulkSelected.filter(d => d !== id));
        } else {
            setBulkSelected([...bulkSelected, id]);
        }
    }, [bulkSelected]);

    const onBulkActionClick = useCallback((action: string | number) => {
        const numberAction = parseInt(action as string);
        if (!paymentMethods)
            return;
        if (numberAction === 1)
            modals.status.setIsComponentVisible(true);
        if (numberAction === 2)
            modals.type.setIsComponentVisible(true);
        if (numberAction === 3)
            modals.provider.setIsComponentVisible(true);
        if (numberAction === 4) {
            if (!listCountries) {
                sendCountriesRequest();
                sendRegionsRequest();
            }
            modals.countries.setIsComponentVisible(true);
        }
        if (numberAction === 5)
            modals.currencies.setIsComponentVisible(true);
        if (numberAction === 6)
            modals.open_in_new_tab.setIsComponentVisible(true);
    }, [modals, listCountries, sendRegionsRequest, sendCountriesRequest]);

    const saveBulkStatus = useCallback(() => {
        if (!bulkStatus || !paymentMethods || !system || !system.system || !system.system.statuses)
            return;
        setBulkInProgress(true);
        const localSelectAll = filterStatus || filterProvider || filterType || (dataTableRequest.search && dataTableRequest.search.length > 0) ? false : selectAll;
        API.bulkSetStatusPaymentMethods(localSelectAll, bulkSelected, bulkStatus).then(() => {
            setBulkInProgress(false);
            setBulkSelected([]);
            setSelectAll(false);
            setBulkStatus(undefined);
            modals.status.setIsComponentVisible(false);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.payment_methods.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.payment_methods.title').toLowerCase()
                }), 'bg-teal-500');
                const statusName = system.system?.statuses.find(s => s.id === bulkStatus);
                const newPaymentMethods = [...paymentMethods.data].map((provider) => {
                    if (bulkSelected.includes(provider.id)) {
                        provider.status = statusName?.name ?? ''
                    }
                    return provider;
                });
                setPaymentMethods({...paymentMethods, data: newPaymentMethods});
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.BULK_PAYMENT_METHODS_STATUS}`, toast, handleError, setIsForbidden));
    }, [selectAll, bulkSelected, bulkStatus, modals.status, paymentMethods, dataTableRequest, sendRequest, system, filterType, filterStatus, filterProvider]);

    const saveBulkType = useCallback(() => {
        if (!bulkType || !paymentMethods || !types)
            return;
        setBulkInProgress(true);
        const localSelectAll = filterStatus || filterProvider || filterType || (dataTableRequest.search && dataTableRequest.search.length > 0) ? false : selectAll;
        API.bulkSetTypePaymentMethods(localSelectAll, bulkSelected, bulkType).then(() => {
            setBulkInProgress(false);
            setBulkSelected([]);
            setSelectAll(false);
            setBulkType(undefined);
            modals.type.setIsComponentVisible(false);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.payment_methods.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.payment_methods.title').toLowerCase()
                }), 'bg-teal-500');
                const typeName = types.data.find(s => s.id === bulkType);
                const newPaymentMethods = [...paymentMethods.data].map((provider) => {
                    if (bulkSelected.includes(provider.id)) {
                        provider.type = typeName?.name ?? ''
                    }
                    return provider;
                });
                setPaymentMethods({...paymentMethods, data: newPaymentMethods});
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.BULK_PAYMENT_METHODS_TYPE}`, toast, handleError, setIsForbidden));
    }, [selectAll, bulkSelected, bulkType, modals.type, paymentMethods, dataTableRequest, sendRequest, system, types, filterType, filterStatus, filterProvider]);

    const saveBulkProvider = useCallback(() => {
        if (!bulkProvider || !paymentMethods || !system || !system.system || !system.system.providers)
            return;
        setBulkInProgress(true);
        const localSelectAll = filterStatus || filterProvider || filterType || (dataTableRequest.search && dataTableRequest.search.length > 0) ? false : selectAll;
        API.bulkSetProviderPaymentMethods(localSelectAll, bulkSelected, bulkProvider).then(() => {
            setBulkInProgress(false);
            setBulkSelected([]);
            setSelectAll(false);
            setBulkProvider(undefined);
            modals.provider.setIsComponentVisible(false);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.payment_methods.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.payment_methods.title').toLowerCase()
                }), 'bg-teal-500');
                const providerName = system.system?.providers.find(s => s.id === bulkProvider);
                const newPaymentMethods = [...paymentMethods.data].map((provider) => {
                    if (bulkSelected.includes(provider.id)) {
                        provider.provider = providerName?.name ?? ''
                    }
                    return provider;
                });
                setPaymentMethods({...paymentMethods, data: newPaymentMethods});
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.BULK_PAYMENT_METHODS_PROVIDER}`, toast, handleError, setIsForbidden));
    }, [selectAll, bulkSelected, bulkProvider, modals.provider, paymentMethods, dataTableRequest, sendRequest, system, filterType, filterStatus, filterProvider]);

    const saveBulkCurrencies = useCallback(() => {
        if (!bulkCurrencies || !paymentMethods || !system || !system.system || !system.system.currencies)
            return;
        setBulkInProgress(true);
        const localSelectAll = filterStatus || filterProvider || filterType || (dataTableRequest.search && dataTableRequest.search.length > 0) ? false : selectAll;
        API.bulkSetCurrenciesPaymentMethods(localSelectAll, bulkSelected, bulkCurrencies).then(() => {
            setBulkInProgress(false);
            setBulkSelected([]);
            setSelectAll(false);
            setBulkCurrencies([]);
            modals.currencies.setIsComponentVisible(false);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.payment_methods.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.payment_methods.title').toLowerCase()
                }), 'bg-teal-500');
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.BULK_PAYMENT_METHODS_CURRENCIES}`, toast, handleError, setIsForbidden));
    }, [selectAll, bulkSelected, bulkCurrencies, modals.provider, paymentMethods, dataTableRequest, sendRequest, system, filterType, filterStatus, filterProvider]);


    const saveBulkCountries = useCallback(() => {
        if (!bulkCountries || !paymentMethods || !listCountries)
            return;
        setBulkInProgress(true);
        const localSelectAll = filterStatus || filterProvider || filterType || (dataTableRequest.search && dataTableRequest.search.length > 0) ? false : selectAll;
        API.bulkSetCountriesPaymentMethods(localSelectAll, bulkSelected, bulkCountries).then(() => {
            setBulkInProgress(false);
            setBulkSelected([]);
            setSelectAll(false);
            setBulkCountries([]);
            modals.countries.setIsComponentVisible(false);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.payment_methods.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.payment_methods.title').toLowerCase()
                }), 'bg-teal-500');
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.BULK_PAYMENT_METHODS_COUNTRIES}`, toast, handleError, setIsForbidden));
    }, [selectAll, bulkSelected, bulkCurrencies, modals.provider, paymentMethods, dataTableRequest, sendRequest, listCountries]);

    const saveBulkNewTab = useCallback(() => {
        if (!paymentMethods)
            return;
        setBulkInProgress(true);
        const localSelectAll = filterStatus || filterProvider || filterType || (dataTableRequest.search && dataTableRequest.search.length > 0) ? false : selectAll;
        API.bulkSetOpenInNewTabPaymentMethods(localSelectAll, bulkSelected, bulkNewTab).then(() => {
            setBulkInProgress(false);
            setBulkSelected([]);
            setSelectAll(false);
            setBulkNewTab(false);
            modals.open_in_new_tab.setIsComponentVisible(false);
            if (selectAll) {
                toast?.show(t('admin.bulk_actions.updated_all', {type: t('admin.payment_methods.title').toLowerCase()}), 'bg-teal-500');
                sendRequest(dataTableRequest);
            } else {
                toast?.show(t('admin.bulk_actions.updated', {
                    count: bulkSelected.length,
                    type: t('admin.payment_methods.title').toLowerCase()
                }), 'bg-teal-500');
            }
        }).catch(e => ErrorHandler(e, `patch${APILink.BULK_PAYMENT_METHODS_COUNTRIES}`, toast, handleError, setIsForbidden));
    }, [selectAll, bulkSelected, bulkNewTab, modals.open_in_new_tab, paymentMethods, dataTableRequest, sendRequest, filterProvider, filterType, filterStatus]);

    useEffect(() => {
        setTitle(t('admin.payment_methods.title'));
        dispatch(setSidebarTitle(t('admin.payment_methods.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendTypesRequest();
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                        return;
                    }
                }
                sendRequest(dataTableRequest);
            }).catch();
        }
    }, [user, dispatch]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);


    const statusModal = useMemo(() => {
        if (!modals.status.isComponentVisible || (!selectAll && !bulkSelected.length) || !system || !system.system || !system.system.statuses)
            return <></>

        return <Modal refer={modals.status.ref} showModal={modals.status.isComponentVisible}
                      title="Bulk update status">
            <div className="max-w-md w-full">
                <DropdownInput className="w-[calc(50% - 2.5rem)] max-w-[14rem]"
                               menuClassName="max-w-[14rem] min-w-[14rem]"
                               name={t('admin.payment_methods.table.status')}
                               selected={bulkStatus}
                               onChange={(s) => setBulkStatus(typeof s === 'string' ? parseInt(s) : s)}
                               list={system.system.statuses ?? []}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={bulkInProgress}
                            onClick={() => saveBulkStatus()}>{bulkInProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => modals.status.setIsComponentVisible(false)}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modals.status, bulkSelected, bulkStatus, selectAll, bulkInProgress, saveBulkStatus, system]);

    const typeModal = useMemo(() => {
        if (!modals.type.isComponentVisible || (!selectAll && !bulkSelected.length) || !types)
            return <></>

        return <Modal refer={modals.type.ref} showModal={modals.type.isComponentVisible}
                      title="Bulk update type">
            <div className="max-w-md w-full">
                <DropdownInput className="w-[calc(50% - 2.5rem)] max-w-[14rem]"
                               menuClassName="max-w-[14rem] min-w-[14rem]"
                               name={t('admin.payment_methods.table.type')}
                               selected={bulkType}
                               onChange={(s) => setBulkType(typeof s === 'string' ? parseInt(s) : s)}
                               list={types.data ?? []}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={bulkInProgress}
                            onClick={() => saveBulkType()}>{bulkInProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => modals.type.setIsComponentVisible(false)}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modals.type, bulkSelected, bulkType, selectAll, bulkInProgress, saveBulkType, types]);

    const providerModal = useMemo(() => {
        if (!modals.provider.isComponentVisible || (!selectAll && !bulkSelected.length) || !system || !system.system || !system.system.providers)
            return <></>

        return <Modal refer={modals.provider.ref} showModal={modals.provider.isComponentVisible}
                      title="Bulk update provider">
            <div className="max-w-md w-full min-h-[24rem]">
                <DropdownInput className="w-[calc(50% - 2.5rem)] max-w-[14rem]"
                               searchable={true}
                               menuClassName="max-w-[14rem] min-w-[14rem]"
                               name={t('admin.payment_methods.table.provider')}
                               selected={bulkProvider}
                               onChange={(s) => setBulkProvider(typeof s === 'string' ? parseInt(s) : s)}
                               list={system.system.providers ?? []}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={bulkInProgress}
                            onClick={() => saveBulkProvider()}>{bulkInProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => modals.provider.setIsComponentVisible(false)}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modals.provider, bulkSelected, bulkProvider, selectAll, bulkInProgress, saveBulkProvider, types]);

    const newTabModal = useMemo(() => {
        if (!modals.open_in_new_tab.isComponentVisible || (!selectAll && !bulkSelected.length) || !system || !system.system || !system.system.providers)
            return <></>

        return <Modal refer={modals.open_in_new_tab.ref} showModal={modals.open_in_new_tab.isComponentVisible}
                      title="Bulk update open in new tab">
            <div className="max-w-md w-full">
                <Checkbox checked={bulkNewTab}
                          onChange={(e: ChangeEvent<HTMLInputElement> | undefined) => {
                              if (e && e.target) {
                                  setBulkNewTab(parseInt(e.target.value) === 1);
                              }
                          }}
                          color={'teal'} crossOrigin={''}
                          value={bulkNewTab ? 0 : 1} label={<span
                    className="dark:text-gray-500">{t('admin.payment_methods.open_in_new_tab')}</span>}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={bulkInProgress}
                            onClick={() => saveBulkNewTab()}>{bulkInProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => modals.open_in_new_tab.setIsComponentVisible(false)}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modals.open_in_new_tab, bulkSelected, bulkNewTab, selectAll, bulkInProgress, saveBulkNewTab]);

    const currenciesModal = useMemo(() => {
        if (!modals.currencies.isComponentVisible || (!selectAll && !bulkSelected.length) || !system || !system.system || !system.system.currencies)
            return <></>

        return <Modal refer={modals.currencies.ref} showModal={modals.currencies.isComponentVisible}
                      title="Bulk update currencies">
            <div className="max-w-md w-full min-h-[24rem]">
                <DropdownInput name={t('admin.payment_methods.table.currencies')}
                               searchable={true}
                               multiple={true}
                               selected={bulkCurrencies}
                               onChange={(s: string | number) => {
                                   const newCurrencies = bulkCurrencies ? [...bulkCurrencies] : [];
                                   setBulkCurrencies([...newCurrencies, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
                                       return array.indexOf(value) === index;
                                   }));
                               }}
                               onRemove={(index: number) => {
                                   const newCurrencies = [...bulkCurrencies];
                                   if (newCurrencies[index]) {
                                       newCurrencies.splice(index, 1)
                                   }
                                   setBulkCurrencies(newCurrencies);
                               }}
                               list={system?.system?.currencies.map((i) => {
                                   return {id: i.id, name: i.code}
                               }) ?? []}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={bulkInProgress}
                            onClick={() => saveBulkCurrencies()}>{bulkInProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => modals.currencies.setIsComponentVisible(false)}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modals.currencies, bulkSelected, bulkCurrencies, selectAll, bulkInProgress, saveBulkCurrencies, system]);

    const countriesModal = useMemo(() => {
        if (!modals.countries.isComponentVisible || (!selectAll && !bulkSelected.length) || !allRegions || !allRegions.data || !listCountries || !listCountries.data)
            return <></>

        return <Modal refer={modals.countries.ref} showModal={modals.countries.isComponentVisible}
                      title="Bulk update countries">
            <div className="max-w-md w-full min-h-[24rem]">
                <DropdownInput id="regions_form" name={t('admin.payment_methods.regions')}
                               className="mb-4"
                               searchable={true}
                               multiple={true}
                               selected={regions}
                               onChange={(s: string | number) => {
                                   if (!allRegions)
                                       return;
                                   const newRegions = regions ? [...regions] : [];
                                   const filteredRegions = [...newRegions, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
                                       return array.indexOf(value) === index;
                                   });
                                   const regionCountries = allRegions.data.filter(i => filteredRegions.includes(i.id)).map(r => r.countries);
                                   let newCountries = [];
                                   for (let i = 0; i < regionCountries.length; i++) {
                                       // @ts-expect-error can be undefined
                                       newCountries = newCountries.concat(regionCountries[i]);
                                   }
                                   setRegions(filteredRegions);
                                   setBulkCountries(newCountries);
                               }}
                               onRemove={(index: number) => {
                                   if (regions && allRegions) {
                                       const newRegions = [...regions];
                                       if (newRegions[index]) {
                                           newRegions.splice(index, 1)
                                       }
                                       setRegions(newRegions);
                                       const regionCountries = allRegions.data.filter(i => newRegions.includes(i.id)).map(r => r.countries);
                                       let newCountries = [];
                                       for (let i = 0; i < regionCountries.length; i++) {
                                           // @ts-expect-error can be undefined
                                           newCountries = newCountries.concat(regionCountries[i]);
                                       }
                                       setBulkCountries(newCountries);
                                   }
                               }}
                               list={allRegions?.data ?? []}/>

                <DropdownInput name={t('admin.payment_methods.table.countries')}
                               searchable={true}
                               multiple={true}
                               withAllButton={true}
                               selected={bulkCountries}
                               onChange={(s: string | number) => {
                                   const newCountries = bulkCountries ? [...bulkCountries] : [];
                                   setBulkCountries([...newCountries, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
                                       return array.indexOf(value) === index;
                                   }));
                               }}
                               onRemove={(index: number) => {
                                   const newCountries = [...bulkCountries];
                                   if (newCountries[index]) {
                                       newCountries.splice(index, 1)
                                   }
                                   setBulkCountries(newCountries);
                               }}
                               onSelectAll={() => setBulkCountries(listCountries?.data.map(i => i.id))}
                               list={listCountries?.data.map((item) => {
                                   return {
                                       id: item.id,
                                       name: item.name,
                                       icon: <CountryAvatar country={item} withName={false}
                                                            className="h-3 max-w-[1rem]"/>
                                   }
                               }) ?? []}/>

                <div className="text-center mt-8">
                    <Button className="bg-teal-500 text-white" disabled={bulkInProgress}
                            onClick={() => saveBulkCountries()}>{bulkInProgress ? <Loader/> : t('Save')}</Button>
                </div>
                <div className="text-center mt-0.5">
                    <span className="text-gray-500 cursor-pointer"
                          onClick={() => modals.countries.setIsComponentVisible(false)}>{t('close')}</span>
                </div>
            </div>
        </Modal>
    }, [modals.countries, bulkSelected, bulkCountries, selectAll, bulkInProgress, saveBulkCountries, listCountries, allRegions, regions]);

    const download = useCallback(() => {
        const request = {
            ...dataTableRequest,
            status: filterStatus,
            provider: filterProvider,
            type: filterType,
        };
        setIsDataTableLoading(true);
        API.downloadPaymentMethodsReport(clearRequest(request)).then((response: AxiosResponse) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', "payment_methods.csv");

            document.body.appendChild(link);
            link.click();

            // @ts-expect-error can be undefined
            link.parentNode.removeChild(link);
            setIsDataTableLoading(false);
        }).catch((e) => {
            setIsDataTableLoading(false);
            ErrorHandler(e, '', toast, handleError)
        });
    }, [dataTableRequest, filterType, filterStatus, filterProvider, toast, handleError]);

    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (system?.system?.statuses && filterStatus > 0) {
            const status = system.system.statuses.find((st) => st.id === filterStatus);
            if (status) {
                chips.push({
                    key: `status-${status.id}`,
                    label: `${t('admin.payment_methods.table.status')}: ${t(status.name)}`,
                    onRemove: () => onStatusChanged('0')
                });
            }
        }
        if (system?.system?.providers && filterProvider > 0) {
            const provider = system.system.providers.find((p) => p.id === filterProvider);
            if (provider) {
                chips.push({
                    key: `provider-${provider.id}`,
                    label: `${t('admin.payment_methods.table.provider')}: ${provider.name}`,
                    onRemove: () => onProviderChanged('0')
                });
            }
        }
        if (types?.data && filterType > 0) {
            const type = types.data.find((tp) => tp.id === filterType);
            if (type) {
                chips.push({
                    key: `type-${type.id}`,
                    label: `${t('admin.payment_methods.table.type')}: ${type.name}`,
                    onRemove: () => onTypeChanged('0')
                });
            }
        }
        return chips;
    }, [system, filterStatus, filterProvider, types, filterType, t, onStatusChanged, onProviderChanged, onTypeChanged]);

    const filters = useMemo(() => {
        if (!types || !system || !system.system || !user || !user.user)
            return <></>;

        return <>
            <DropdownInput
                withAllButton={true}
                searchable={true}
                name={t('admin.payment_methods.table.provider')}
                selected={filterProvider}
                onChange={onProviderChanged}
                onSelectAll={() => onProviderChanged('')}
                list={system.system.providers}
                menuClassName="w-full -mt-5"
            />
            <DropdownInput
                withAllButton={true}
                name={t('admin.payment_methods.table.type')}
                selected={filterType}
                onChange={onTypeChanged}
                onSelectAll={() => onTypeChanged('')}
                list={types.data}
                menuClassName="w-full -mt-5"
            />
            <DropdownInput
                withAllButton={true}
                name={t('admin.languages.table.status')}
                selected={filterStatus}
                onChange={onStatusChanged}
                onSelectAll={() => onStatusChanged('')}
                list={system.system.statuses}
                menuClassName="w-full -mt-5"
            />
        </>
    }, [types, system, filterType, filterStatus, filterProvider, download, onTypeChanged, onProviderChanged, onStatusChanged, user]);

    const extraFilters = useMemo(() => {
        return <>
            {bulkSelected.length > 0 ?
                <FilterDropdown list={[
                    {id: 1, name: 'Change status'},
                    {id: 2, name: 'Change type'},
                    {id: 3, name: 'Change provider'},
                    {id: 4, name: 'Change countries'},
                    {id: 5, name: 'Change currencies'},
                    {id: 6, name: 'Change open in new tab'},
                ]} name={t('admin.bulk_actions.title')}
                                onChange={onBulkActionClick}
                                searchable={false}
                                withAllElement={false}
                                className="mb-2 lg:mb-0"/> : ''}
        </>
    }, [bulkSelected, onBulkActionClick, t]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('download payment methods report') ?
                    <Button className="bg-black px-0.5" onClick={() => download()}><FontAwesomeIcon
                        icon={solid('download')}
                        className="w-12 h-3"/></Button> : ''}
                {user?.user.permissions.includes('create payment method') &&
                    <Button onClick={() => navigate('/app/admin/payment_methods/create')}
                            className="bg-black text-white">{t('admin.payment_methods.new.button')}</Button>}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!paymentMethods)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={paymentMethods.pages.total}
                          count={paymentMethods.count}
                          nodata={paymentMethods.data.length === 0}
                          inProgress={isDataTableLoading}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          extraFilters={extraFilters}
                          rightPanel={actionsPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {
                name: 'check_all',
                element: <Checkbox color={'teal'} crossOrigin={''} checked={selectAll} onChange={onClickSelectAll}/>,
                className: 'text-center px-[0px!important]'
            },
            {name: t('admin.payment_methods.table.name'), sortBy: 'name'},
            {name: t('admin.payment_methods.table.status'), sortBy: 'status_id'},
            {name: t('admin.payment_methods.table.provider'), sortBy: 'provider_id'},
            {name: t('admin.payment_methods.table.type'), sortBy: 'type_id'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {paymentMethods.data.map((paymentMethod, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD className="w-2 px-[0px!important]">
                            <Checkbox color={'teal'} crossOrigin={''} checked={bulkSelected.includes(paymentMethod.id)}
                                      onChange={() => onCheck(paymentMethod.id)}/>
                        </TD>
                        <TD>
                            <div className="flex items-center justify-center">
                                <ProviderAvatar
                                    image={`${window.location.origin}/images/psps/${paymentMethod.image}`}/>
                                {paymentMethod.name}
                            </div>
                        </TD>
                        <TD><StatusLabel status={paymentMethod.status}/></TD>
                        <TD>{paymentMethod.provider}</TD>
                        <TD>{paymentMethod.type}</TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update payment method') &&
                                    <Button onClick={() => navigate(`/app/admin/payment_methods/${paymentMethod.id}`)}
                                            className="px-5 py-2 text-white bg-black lg:mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete payment method') &&
                                    <Button onClick={() => deletePaymentMethod(paymentMethod)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, extraFilters, isDataTableLoading, paymentMethods, deletePaymentMethod, navigate, onDataTableChanged, filters, filterChipsItems, user?.user.permissions]);

    return (
        <PageBlock>
            <Card className="6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    {statusModal}
                    {typeModal}
                    {providerModal}
                    {newTabModal}
                    {currenciesModal}
                    {countriesModal}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(PaymentMethods);
